import { Component, EventEmitter, Output, Input, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterModule } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { catchError, timeout } from 'rxjs/operators';
import { throwError, firstValueFrom } from 'rxjs';

interface LoginResponse {
  status?: string;
  success?: boolean;
  message?: string;
  data?: {
    id?: string;
    username?: string;
    token?: string;
  };
}

@Component({
  selector: 'app-login-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    RouterModule
  ],
  templateUrl: './login-modal.component.html',
  styleUrls: ['./login-modal.component.css']
})
export class LoginModalComponent {
  @Input() isVisible = false;
  @Output() loginSuccess = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  username = '';
  password = '';
  hidePassword = true;
  isLoading = false;
  errorMessage = '';

  // Base URL - change this to match your backend
  private readonly LOGIN_URL = 'https://aadinathapi.beatsacademy.in/admin/login/';

  constructor(
    private http: HttpClient,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  onModalBackdropClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  onClose(): void {
    this.close.emit();
    this.resetForm();
  }

  async onSubmit(): Promise<void> {
    if (this.isLoading || !this.username.trim() || !this.password.trim()) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const credentials = {
      username: this.username.trim(),
      password: this.password
    };

    try {
      const response = await firstValueFrom(
        this.http.post<LoginResponse>(this.LOGIN_URL, credentials, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }).pipe(
          timeout(10000),
          catchError((error: HttpErrorResponse) => {
            console.error('Login Error Details:', error);
            
            // Check if response is HTML instead of JSON
            if (error.error && typeof error.error === 'string' && error.error.includes('<!DOCTYPE')) {
              return throwError(() => new Error('Server returned HTML page instead of JSON. Please check if the backend API is properly configured.'));
            }
            
            // Handle different HTTP status codes
            if (error.status === 0) {
              return throwError(() => new Error('Cannot connect to server. Please check if the backend is running on ' + this.LOGIN_URL));
            } else if (error.status === 404) {
              return throwError(() => new Error('Login endpoint not found. Backend may not be properly configured.'));
            } else if (error.status === 401) {
              return throwError(() => new Error('Invalid username or password.'));
            } else if (error.status === 500) {
              return throwError(() => new Error('Server error. Please check backend logs.'));
            } else {
              return throwError(() => new Error(`HTTP ${error.status}: ${error.message}`));
            }
          })
        )
      );

      // Handle successful response
      if (response && (response.status === 'success' || response.success)) {
        this.handleSuccessfulLogin(response);
      } else {
        this.errorMessage = response?.message || 'Login failed. Invalid response from server.';
      }

    } catch (error: any) {
      console.error('Login failed:', error);
      this.errorMessage = error.message || 'Login failed. Please try again.';
      
      // If API fails completely, offer mock login for development
      if (error.message.includes('connect') || error.message.includes('HTML')) {
        this.errorMessage += '\n\nFor development: You can use mock login (admin/admin123)';
      }
    } finally {
      this.isLoading = false;
    }
  }

  private handleSuccessfulLogin(response: LoginResponse): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem('id', response.data?.id || '1');
        localStorage.setItem('username', response.data?.username || this.username);

        if (response.data?.username === 'admin' || this.username === 'admin') {
          localStorage.setItem('adminToken', 'admin-token');
        }
      } catch (storageError) {
        console.warn('localStorage not available:', storageError);
      }
    }

    this.loginSuccess.emit();
    
    setTimeout(() => {
      this.onClose();
      
      if (response.data?.username === 'admin' || this.username === 'admin') {
        this.router.navigate(['/admin/dashboard']);
      } else {
        this.router.navigate(['/']);
      }
    }, 100);
  }

  // Mock login for development when API is not available
  mockLogin(): void {
    if (this.username === 'admin' && this.password === 'admin123') {
      const mockResponse: LoginResponse = {
        status: 'success',
        data: {
          id: '1',
          username: 'admin'
        }
      };
      
      this.handleSuccessfulLogin(mockResponse);
    } else {
      this.errorMessage = 'Mock login requires: admin/admin123';
    }
  }

  private resetForm(): void {
    this.username = '';
    this.password = '';
    this.errorMessage = '';
    this.isLoading = false;
    this.hidePassword = true;
  }
}
